import { ApiError, estClient } from './estClient';

export interface CustomField {
  id: string;
  name: string;
  slug: string;
  type: string; // '1' text | '2' number | '3' date | '4' choice — see customFieldApi.TYPE
  field_for: string; // '1' unit | '2' lead | '3' deal | '4' deal_cancellation | '10' document — see customFieldApi.FIELD_FOR
  data: string[] | null; // present/used only when type === TYPE.CHOICE
  created_at: string;
}

export type NewCustomFieldProps = Pick<
  CustomField,
  'name' | 'type' | 'field_for'
> & {
  slug?: string; // optional — backend derives it from `name` if omitted
  data?: string[]; // required in practice when type === TYPE.CHOICE
};

export type UpdateCustomFieldProps = Pick<
  CustomField,
  'name' | 'type' | 'field_for'
> & {
  data?: string[]; // slug is NOT part of the update payload — immutable server-side
};

interface CustomFieldFailureResponse {
  status: false;
  message: string;
  count?: number; // present only on delete's "in use" failure
}

class CustomFieldApi {
  async getAll({ fieldFor }: { fieldFor: string }): Promise<CustomField[]> {
    return await estClient.get<CustomField[]>(
      `/api/customization/all_custom_field/${fieldFor}`,
    );
  }

  async create(props: NewCustomFieldProps): Promise<CustomField> {
    const resp = await estClient.post<CustomField | CustomFieldFailureResponse>(
      `/api/customization/create_custom_field/${props.field_for}`,
      { body: props },
    );

    assertNotFailure<CustomField>(resp);

    return resp;
  }

  async update(
    id: string,
    props: UpdateCustomFieldProps,
  ): Promise<CustomField> {
    const resp = await estClient.post<CustomField | CustomFieldFailureResponse>(
      `/api/customization/update_custom_field/${id}`,
      { body: props },
    );

    assertNotFailure<CustomField>(resp);

    return resp;
  }

  async delete(id: string): Promise<void> {
    const resp = await estClient.post<null | CustomFieldFailureResponse>(
      `/api/customization/delete_custom_field/${id}`,
    );

    assertNotFailure<null>(resp);
  }

  get TYPE() {
    return {
      TEXT: '1',
      NUMBER: '2',
      DATE: '3',
      CHOICE: '4',
    } as const;
  }

  get FIELD_FOR() {
    return {
      UNIT: '1',
      LEAD: '2',
      DEAL: '3',
      DEAL_CANCELLATION: '4',
      DOCUMENT: '10',
    } as const;
  }

  get SLUG_PREFIX() {
    return {
      '1': 'unit_',
      '2': 'lead_',
      '3': 'deal_',
      '4': 'deal_c_',
      '10': '',
    } as const;
  }
}

/**
 * Success responses for create/update/delete carry no `status` key at all
 * (create/update return the raw entity, delete returns an empty body i.e. `null`) —
 * only the two documented failure cases carry `status: false`. A bare
 * `if (!resp.status)` check (as used elsewhere, e.g. userApi.ts) would throw on
 * every successful create/update and crash on every successful delete.
 */
function assertNotFailure<T>(
  resp: T | CustomFieldFailureResponse,
): asserts resp is T {
  if (
    resp &&
    typeof resp === 'object' &&
    'status' in resp &&
    resp.status === false
  ) {
    throw new ApiError({
      body: { ...resp, success: false },
    });
  }
}

export const customFieldApi = new CustomFieldApi();
